import React, { useEffect, useCallback, useState } from 'react';
import { SHUFTI_BOTTOM_CONTENT_ID, VERIFICATION_STATUSES_DATA } from './constants';
import { getVerificationStatus } from './helpers';
import { useTranslations } from '@core/js/hooks/i18n';
import { clsx } from '@core/js/utils/classNames.util';
import ShuftiVerificationButton from './ShuftiVerificationButton';
import { ShuftiConfig } from './types';
import { SHUFTI_STATUS, SHUFTI_VARIANTS } from './enums';
import { useGetPlayerTags } from 'server';
import { WebContent } from '@core/js/components/system/WebContent';

import style from './index.scss';

type ShuftiContentProps = {
    setVerificationUrl: (url: string) => void;
    config: ShuftiConfig;
};

const ShuftiContent: React.FC<ShuftiContentProps> = ({ setVerificationUrl, config }) => {
    const [verificationStatus, setVerificationStatus] = useState('');

    const t = useTranslations('playtech.mobile.system.shufti.');
    const getPlayerTags = useGetPlayerTags();

    const updateVerificationStatus = useCallback(async () => {
        const status = await getVerificationStatus(getPlayerTags);
        setVerificationStatus(status);
    }, [setVerificationStatus, getPlayerTags]);

    useEffect(() => {
        updateVerificationStatus();
    }, [updateVerificationStatus]);

    let verificationConfig = VERIFICATION_STATUSES_DATA[SHUFTI_STATUS.INVALID];
    if (Object.keys(VERIFICATION_STATUSES_DATA).includes(verificationStatus)) {
        verificationConfig = VERIFICATION_STATUSES_DATA[verificationStatus];
    }

    return (
        <>
            <div className={style.container}>
                <div className={style.title}>{t('title')}</div>
                <div className={style.verificationTypes}>
                    <div className={style.verification}>
                        <div className={clsx([style.icon, style.documents])}></div>
                        <div className={style.verificationContainer}>
                            <div className={style.header}>
                                <div className={style.headerTitle}>{t('documentsVerification.title')}</div>
                                {verificationConfig.status && (
                                    <div className={clsx([style.statusLabel, style[verificationStatus]])}>
                                        {t(`status.${verificationStatus}`)}
                                    </div>
                                )}
                            </div>
                            <div className={clsx([style.content, style[verificationStatus]])}>
                                {verificationConfig.congratsSection && (
                                    <div className={style.congratulations}>{t('congratulations')}</div>
                                )}
                                {t(verificationConfig.contentTranslation)}
                            </div>
                            <ShuftiVerificationButton
                                config={config}
                                settings={verificationConfig.button}
                                setVerificationUrl={setVerificationUrl}
                                variant={verificationConfig?.button?.variant || SHUFTI_VARIANTS.DEFAULT}
                            />
                        </div>
                    </div>
                    <div className={style.verification}>
                        <div className={clsx([style.icon, style.bank])}></div>
                        <div className={style.verificationContainer}>
                            <div className={style.header}>
                                <div className={style.headerTitle}>{t('bankVerification.title')}</div>
                            </div>
                            <div className={clsx([style.content, style[verificationStatus]])}>
                                {t('bankVerification.content')}
                            </div>
                            <ShuftiVerificationButton variant={SHUFTI_VARIANTS.EMAIL} />
                        </div>
                    </div>
                </div>
                <div className={style.information}>
                    {t('info')} <ShuftiVerificationButton variant={SHUFTI_VARIANTS.LINK} />
                </div>
            </div>
            <WebContent id={SHUFTI_BOTTOM_CONTENT_ID}></WebContent>
        </>
    );
};

export default ShuftiContent;
